import { AnimatedSprite, Container, Sprite } from 'pixi.js';
import { PLAYER } from '@/config/player';
import { SUPERSHOT, WEAPONS } from '@/config/weapons';
import { SOCKET_MOUNTS, SOCKET_MOUNT_SCALE } from '@/config/weapon-visuals';
import { getFrames, getTexture, makeSprite } from '@/assets/resolver';
import type { InputState } from '@/input/types';
import type { BulletSystem } from './bullets';
import type { Arena } from '@/arena/arena';
import { WeaponHolder } from './weapons';
import type { Rng } from '@/core/rng';
import { PlayerStats } from '@/upgrades/stats';
import { CARD_VALUES } from '@/config/cards';
import { sfx } from '@/audio/sfx';
import { lerpAngle, resolveMove } from './player-steer';

/**
 * Player ship: movement (2 modes), rotation, weapon fire, supershot,
 * dash + Q/E side dodges. Visual layers: shadow (separate world layer) →
 * thrust → hull → socket pods (one per equipped weapon) → wing pods.
 */
export class Player {
  x = 0;
  y = 0;
  prevX = 0;
  prevY = 0;
  vx = 0;
  vy = 0;
  facing = 0;

  /** Supershot energy 0..max — wing pods glow when full */
  energy = 0;
  hp: number = PLAYER.maxHp;
  /** Armor units (yellow layer) — each unit fully eats one hit (docs/12 C1) */
  armor = 0;

  readonly weapon = new WeaponHolder();
  readonly stats = new PlayerStats();

  private dashCooldown = 0;
  private dashTime = 0;
  private dashDirX = 0;
  private dashDirY = 0;
  iframes = 0;
  /** Environmental time-scale on movement (Chronos zones — B15). Reset each frame. */
  envSpeedMul = 1;

  readonly view = new Container();
  readonly shadow: Sprite;
  private readonly thrust: AnimatedSprite;
  /** One on-hull pod per weapon socket E1..E3, category tinted */
  private readonly mountSprites: Sprite[];
  private readonly pods: Sprite;
  private shieldBubble!: Sprite;

  constructor() {
    this.shadow = makeSprite('player/shadow');

    this.thrust = new AnimatedSprite(getFrames('player/ship/thrust'));
    this.thrust.animationSpeed = 0.35;
    this.thrust.anchor.set(0.85, 0.5);
    this.thrust.position.set(-26, 0);
    this.thrust.play();

    this.shieldBubble = makeSprite('boss/hive/shield');
    this.shieldBubble.scale.set(0.55);
    this.shieldBubble.visible = false;

    this.mountSprites = SOCKET_MOUNTS.map((p) => {
      const pod = makeSprite(`player/mount-${this.weapon.category}`);
      pod.position.set(p.x, p.y);
      pod.scale.set(SOCKET_MOUNT_SCALE);
      return pod;
    });
    const ship = makeSprite('player/ship/base');
    this.pods = makeSprite('player/ship/pods');

    this.view.addChild(this.thrust, ship, ...this.mountSprites, this.pods, this.shieldBubble);
    this.refreshMount();
    // 128px canvases scaled to a ~45px ship so visuals match collision radii
    this.view.scale.set(PLAYER.visualScale);
    this.shadow.scale.set(PLAYER.visualScale);
  }

  get isDashing(): boolean {
    return this.dashTime > 0;
  }

  /** 0..1 dash recharge (1 = ready) — HUD dash indicator. */
  get dashReadyPct(): number {
    return Math.min(1, Math.max(0, 1 - this.dashCooldown / (PLAYER.dash.cooldown * this.stats.dashCooldownMul)));
  }

  get energyFull(): boolean {
    return this.energy >= PLAYER.maxEnergy;
  }

  spawnAt(x: number, y: number): void {
    this.x = this.prevX = x;
    this.y = this.prevY = y;
  }

  addEnergy(amount: number): void {
    this.energy = Math.min(PLAYER.maxEnergy, this.energy + amount);
  }

  get maxHp(): number {
    return PLAYER.maxHp + this.stats.bonusHp * PLAYER.cellHp;
  }

  /** Take a hit (i-frames protect; armor units eat hits first). @returns true → player died */
  takeHit(damage: number): boolean {
    if (this.iframes > 0) return false;
    if (this.armor > 0) {
      this.armor--;
      this.iframes = PLAYER.iframesOnHit * 0.6 * this.stats.iframeMul;
      return false;
    }
    this.hp -= this.mitigated(damage);
    this.iframes = PLAYER.iframesOnHit * this.stats.iframeMul;
    return this.hp <= 0;
  }

  /** Incoming HP damage after the defense cards (docs/12 C5), min 1. */
  mitigated(damage: number): number {
    let mul = this.stats.damageTakenMul;
    // Aegis Link: damage reduction scales with the OFFENSE bonus
    if (this.stats.aegisLink) {
      mul *= 1 - Math.min(
        CARD_VALUES.aegis.cap,
        (this.stats.damageMul - 1) * CARD_VALUES.aegis.ratio,
      );
    }
    return Math.max(1, Math.round(damage * mul));
  }

  resetVitals(): void {
    this.hp = PLAYER.maxHp;
    this.energy = 0;
    this.iframes = 0;
    this.armor = 0;
    this.vx = this.vy = 0;
  }

  /** Full run reset: stats + weapon + vitals */
  resetRun(): void {
    this.stats.reset();
    this.weapon.reset();
    this.refreshMount();
    this.resetVitals();
  }

  setEquipped(ids: ReadonlyArray<string>, inventory: ReadonlyArray<string>): void {
    this.weapon.equipped = [...ids];
    this.weapon.sanitize(inventory);
    this.refreshMount();
  }

  /** Every equipped socket gets its own pod, full strength (docs/09). */
  private refreshMount(): void {
    this.mountSprites.forEach((pod, i) => {
      const id = this.weapon.equipped[i];
      const def = id ? WEAPONS[id] : undefined;
      pod.visible = def !== undefined;
      if (def) pod.texture = getTexture(`player/mount-${def.category}`);
    });
  }

  /** Start a dash toward the given world direction (shared cooldown). */
  private startDash(dirX: number, dirY: number): void {
    this.dashDirX = dirX;
    this.dashDirY = dirY;
    this.dashTime = PLAYER.dash.duration;
    this.dashCooldown = PLAYER.dash.cooldown * this.stats.dashCooldownMul;
    this.iframes = Math.max(
      this.iframes,
      PLAYER.dash.iframes + this.stats.dashIframeBonus,
    );
  }

  update(dt: number, input: InputState, bullets: BulletSystem, arena: Arena, rng: Rng): void {
    this.prevX = this.x;
    this.prevY = this.y;

    this.dashCooldown -= dt;
    this.iframes -= dt;
    this.weapon.update(dt);
    this.energy = Math.min(PLAYER.maxEnergy, this.energy + SUPERSHOT.energyRegen * dt);

    const { mx, my } = resolveMove(input, this.facing);

    // Dash (movement dir / facing) and Q/E side dodges (perpendicular to facing)
    if (this.dashCooldown <= 0 && !this.isDashing) {
      if (input.dashPressed) {
        const len = Math.hypot(mx, my);
        this.startDash(
          len > 0 ? mx / len : Math.cos(this.facing),
          len > 0 ? my / len : Math.sin(this.facing),
        );
      } else if (input.dodgeLeftPressed || input.dodgeRightPressed) {
        const side = input.dodgeLeftPressed ? -1 : 1;
        const a = this.facing + (side * Math.PI) / 2;
        this.startDash(Math.cos(a), Math.sin(a));
      }
    }

    if (this.isDashing) {
      this.dashTime -= dt;
      this.vx = this.dashDirX * PLAYER.dash.speed;
      this.vy = this.dashDirY * PLAYER.dash.speed;
    } else {
      this.vx += mx * PLAYER.accel * dt;
      this.vy += my * PLAYER.accel * dt;

      const maxSpeed = PLAYER.maxSpeed * this.stats.speedMul;
      const speed = Math.hypot(this.vx, this.vy);
      if (speed > maxSpeed) {
        this.vx = (this.vx / speed) * maxSpeed;
        this.vy = (this.vy / speed) * maxSpeed;
      }
      const decay = Math.exp(-PLAYER.friction * dt);
      if (mx === 0) this.vx *= decay;
      if (my === 0) this.vy *= decay;
    }

    this.x += this.vx * dt * this.envSpeedMul;
    this.y += this.vy * dt * this.envSpeedMul;
    if (arena.clampCircle(this, PLAYER.radius)) {
      this.dashTime = 0;
    }

    // Rotation — aim has priority, else velocity direction
    const targetAngle = input.hasAim
      ? Math.atan2(input.aimY, input.aimX)
      : Math.hypot(this.vx, this.vy) > 20
        ? Math.atan2(this.vy, this.vx)
        : this.facing;
    this.facing = lerpAngle(this.facing, targetAngle, Math.min(1, PLAYER.turnLerp * dt));

    const aimX = input.hasAim ? input.aimX : Math.cos(this.facing);
    const aimY = input.hasAim ? input.aimY : Math.sin(this.facing);

    if (input.supershot && this.energyFull) {
      this.weapon.fireSupershot(bullets, this.x, this.y, this.facing, aimX, aimY, this.stats, rng);
      this.energy = 0;
      sfx.supershot();
    } else if (input.fire) {
      if (this.weapon.tryFire(bullets, this.x, this.y, this.facing, aimX, aimY, this.stats, rng)) {
        sfx.shoot(this.weapon.category);
      }
    }
  }

  render(alpha: number): void {
    const rx = this.prevX + (this.x - this.prevX) * alpha;
    const ry = this.prevY + (this.y - this.prevY) * alpha;

    this.view.position.set(rx, ry);
    this.view.rotation = this.facing;

    // Shadow: fixed light direction (bottom-right)
    this.shadow.position.set(rx + 10, ry + 16);
    this.shadow.rotation = this.facing;
    this.shadow.alpha = 0.8;

    const speed = Math.hypot(this.vx, this.vy);
    this.thrust.visible = speed > 40;
    this.thrust.scale.set(Math.min(1, speed / PLAYER.maxSpeed) * (this.isDashing ? 1.5 : 1));

    // Wing pods: energy charge frames 0-4
    const frame = Math.min(4, Math.floor((this.energy / PLAYER.maxEnergy) * 4 + 0.0001));
    this.pods.texture = getTexture('player/ship/pods', this.energyFull ? 4 : frame);

    // Shield pickup bubble — counter-rotates so it reads as a field, not hull
    this.shieldBubble.visible = this.armor > 0;
    if (this.armor > 0) {
      this.shieldBubble.rotation = -this.facing + performance.now() / 900;
      this.shieldBubble.alpha = 0.4 + Math.min(0.45, this.armor * 0.08);
      this.shieldBubble.tint = 0xf5c542; // armor reads YELLOW (docs/12 E1)
    }

    this.view.alpha = this.iframes > 0 ? 0.6 : 1;
  }
}
